import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import type Link from 'ember-link/link';
import { localCopy } from 'tracked-toolbox';

const ROUNDED_MAP = {
  full: 'rounded-full',
  large: 'rounded-lg',
  medium: 'rounded-md',
};

export interface MonogramOrPhotoSignature<T> {
  Args: T & {
    /**
     * Name used to compute monogram
     */
    name?: string;
    /**
     * URL of photo
     */
    devicePhoto?: string;
    /**
     * URL of photo
     */
    originalDevicePhoto?: string;
    /**
     * URL of photo
     */
    passportPhoto?: string | null;
    href?: Link | string | null;
    /**
     * Classes for the monogram
     */
    monogramClasses?: string;
    large?: boolean;
    medium?: boolean;
    borderBottom?: boolean;
    align?: 'left' | 'center' | 'right' | 'align-top';
    /**
     * Border radius variant for the photo or monogram
     */
    rounded?: keyof typeof ROUNDED_MAP;
  };
  Blocks: { default: [] };
  Element: HTMLAnchorElement | HTMLDivElement;
}

export default class MonogramOrPhoto<T> extends Component<MonogramOrPhotoSignature<T>> {
  @localCopy('args.borderBottom', true) declare borderBottom: this['args']['borderBottom'];
  @localCopy('args.monogramClasses', '') declare monogramClasses: this['args']['monogramClasses'];

  get href(): string | null {
    /* If @href is a Link, we want to coerce it to a string; it has a `.toString` method that returns a plain string
     * but the type system isn't aware of that.
     */
    if (!this.args.href) return null;
    return String(this.args.href);
  }

  get thumbnailPath(): SafeString | string | undefined {
    const { devicePhoto: smallThumbnail, passportPhoto: userAvatar } = this.args;

    if (smallThumbnail) {
      return htmlSafe(smallThumbnail);
    } else if (userAvatar && !/assets\/pixel.*png/.test(userAvatar)) {
      return userAvatar;
    }
    return undefined;
  }

  get originalDevicePhotoPath(): SafeString | undefined {
    const originalDevicePhoto = this.args.originalDevicePhoto;

    if (originalDevicePhoto) {
      return htmlSafe(originalDevicePhoto);
    }

    return undefined;
  }

  get monogramInitials(): string {
    let nameAttr = this.args.name ?? '';

    if (/\+\d+/.test(nameAttr)) {
      nameAttr = nameAttr.split('+')[0]!;
    }

    const nameArray = nameAttr.split(' ');
    let name = '';
    for (let i = 0; i < nameArray.length; i++) {
      // Skip leading non-alphabetic characters (e.g. "(", "[") and use the first letter of the word.
      const firstLetter = nameArray[i]?.match(/\p{L}/u)?.[0] ?? '';
      name += firstLetter;
    }
    return name.slice(0, 4); // max 4 chars
  }

  get lengthClass(): string {
    const monogramInitials = this.monogramInitials;

    switch (monogramInitials.length) {
      case 2:
        return 'two';
      case 3:
        return 'three';
      case 4:
        return 'four';
    }
    return '';
  }

  get monogramHueValue(): number {
    let nameAttr = this.args.name ?? '';

    if (/\+\d+/.test(nameAttr)) {
      nameAttr = nameAttr.split('+')[0]!;
    }

    const strippedName = nameAttr.replace(/\s+/g, '');
    let asciiSum = 0;
    for (let i = 0; i < strippedName.length; i++) {
      asciiSum += strippedName.charCodeAt(i);
    }
    const hueValue = asciiSum % 360;
    return hueValue;
  }

  get safeStyle(): SafeString {
    const bg = `background-color: hsl(${this.monogramHueValue}, 33%, 45%)`;

    return htmlSafe(bg);
  }

  get safeImageStyle(): SafeString {
    const { medium } = this.args;
    const dimensions = medium ? `width: 40px; height: 40px;` : '';

    return htmlSafe(dimensions);
  }

  get roundedClass(): string {
    return (this.args.rounded && ROUNDED_MAP[this.args.rounded]) || ROUNDED_MAP.full;
  }

  <template>
    {{#if this.thumbnailPath}}
      {{#if @large}}
        <a
          class="block w-[100px] h-[100px] overflow-hidden {{this.roundedClass}}"
          aria-hidden="true"
          href={{if this.originalDevicePhotoPath this.originalDevicePhotoPath this.thumbnailPath}}
          target="_blank"
          rel="noopener"
          data-test-entry-thumbnail-link
          ...attributes
        >
          <img
            alt=""
            src={{this.thumbnailPath}}
            class="object-cover object-center aspect-square"
            role="presentation"
            data-test-entry-photo
            data-test-entry-thumbnail
          />
        </a>
      {{else}}
        {{#if this.href}}
          <a
            aria-hidden="true"
            href={{this.href}}
            class="table-cell w-[50px] {{if this.borderBottom 'border-b'}} {{if @align @align 'align-middle'}} p-2"
            ...attributes
          >
            <span
              class="block mx-auto overflow-hidden w-[50px] h-[50px] {{this.roundedClass}}"
              style={{this.safeImageStyle}}
            >
              <img
                alt=""
                class="object-cover object-center aspect-square"
                src={{this.thumbnailPath}}
                role="presentation"
                data-test-entry-feed-photo
                data-test-entry-thumbnail
              />
            </span>
          </a>
        {{else}}
          <div
            class="table-cell w-[50px] {{if this.borderBottom 'border-b'}} {{if @align @align 'align-middle'}} p-2"
            ...attributes
          >
            <span
              class="block mx-auto overflow-hidden w-[50px] h-[50px] {{this.roundedClass}}"
              style={{this.safeImageStyle}}
            >
              <img
                alt=""
                class="object-cover object-center aspect-square"
                src={{this.thumbnailPath}}
                role="presentation"
                data-test-entry-feed-photo
                data-test-entry-thumbnail
              />
            </span>
          </div>
        {{/if}}
      {{/if}}
    {{else}}
      {{#if @large}}
        <div
          data-test-entry-monogram
          class="monogram large {{this.monogramClasses}}"
          style={{this.safeStyle}}
          ...attributes
        ></div>
      {{else if @medium}}
        {{#if this.href}}
          <a
            aria-hidden="true"
            href={{this.href}}
            class="table-cell
              {{if this.borderBottom 'border-b'}}
              {{if @align @align 'align-middle'}}
              p-2 no-underline w-[50px] min-w-[50px]"
            ...attributes
          >
            <div
              data-test-entry-monogram
              class="monogram medium {{this.monogramClasses}} {{this.lengthClass}}"
              style={{this.safeStyle}}
            >
              {{this.monogramInitials}}
            </div>
          </a>
        {{else}}
          <div
            aria-hidden="true"
            class="table-cell
              {{if this.borderBottom 'border-b'}}
              {{if @align @align 'align-middle'}}
              p-2 w-[50px] min-w-[50px]"
            ...attributes
          >
            <div
              data-test-entry-monogram
              class="monogram medium {{this.monogramClasses}} {{this.lengthClass}}"
              style={{this.safeStyle}}
            >
              {{this.monogramInitials}}
            </div>
          </div>
        {{/if}}
      {{else}}
        {{#if this.href}}
          <a
            aria-hidden="true"
            href={{this.href}}
            class="table-cell
              {{if this.borderBottom 'border-b'}}
              {{if @align @align 'align-middle'}}
              p-2 no-underline w-[50px] min-w-[50px]"
            ...attributes
          >
            <div
              data-test-entry-monogram
              class="monogram {{this.monogramClasses}} {{this.lengthClass}}"
              style={{this.safeStyle}}
            >
              {{this.monogramInitials}}
            </div>
          </a>
        {{else}}
          <div
            aria-hidden="true"
            class="table-cell
              {{if this.borderBottom 'border-b'}}
              {{if @align @align 'align-middle'}}
              p-2 w-[50px] min-w-[50px]"
            ...attributes
          >
            <div
              data-test-entry-monogram
              class="monogram {{this.monogramClasses}} {{this.lengthClass}}"
              style={{this.safeStyle}}
            >
              {{this.monogramInitials}}
            </div>
          </div>
        {{/if}}
      {{/if}}
    {{/if}}
  </template>
}
